import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';

export const API_BASE_URL = (import.meta.env.VITE_API_URL || 'http://localhost:9229').replace(/\/$/, '');

const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
});

// Request queue for handling concurrent requests during token refresh
interface PendingRequest {
  config: InternalAxiosRequestConfig;
  resolve: (value: any) => void;
  reject: (reason?: any) => void;
}

let isRefreshing = false;
let failedQueue: PendingRequest[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  isRefreshing = false;
  failedQueue = [];
};

// Request interceptor - add access token to all requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor - handle 401 errors with token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // Handle 401 Unauthorized responses
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // Queue the request if a refresh is already in progress
        return new Promise<any>((resolve, reject) => {
          failedQueue.push({
            config: originalRequest,
            resolve,
            reject,
          });
        })
          .then((token) => {
            if (token) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (!refreshToken) {
          throw new Error('No refresh token available');
        }

        // Bare axios on purpose — avoids re-entering this interceptor if the refresh itself 401s
        const { data } = await axios.post(
          `${API_BASE_URL}/auth/refresh`,
          { refreshToken }
        );

        const newAccessToken = data.session.access_token;
        const newRefreshToken = data.session.refresh_token;

        // Update tokens in localStorage
        localStorage.setItem('access_token', newAccessToken);
        localStorage.setItem('refresh_token', newRefreshToken);
        localStorage.setItem('fieldOsAccessToken', newAccessToken);

        // Update authorization header
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

        // Process queued requests with new token
        processQueue(null, newAccessToken);

        return api(originalRequest);
      } catch (refreshError) {
        // Refresh failed - clear tokens and redirect to login
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('fieldOsAccessToken');

        // Also clear the persisted session so userDetails can't outlive the tokens.
        // Otherwise ProtectedRoute would treat the next cold start as logged-in,
        // then immediately 401 again -> the "redirected after opening a few times" bug.
        try {
          const stored = localStorage.getItem('field-os-data');
          if (stored) {
            const data = JSON.parse(stored);
            data.userDetails = null;
            data.user = null;
            localStorage.setItem('field-os-data', JSON.stringify(data));
          }
        } catch {
          localStorage.removeItem('field-os-data');
        }

        processQueue(refreshError, null);
        window.location.href = '/login';
        
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

export default api;